import { apiClient } from "@/lib/api/client";
import { AppHttpError } from "@/lib/api/errors";
import type {
  ActiveAlertSummaryDto,
  AlertDetailDto,
  AlertDto,
  AlertRuleCreateDto,
  AlertRuleDto,
} from "@/lib/api/types";

export type ListAlertsParams = {
  deviceId?: string;
  tenantId?: string;
  workspaceScope?: "all";
  status?: "active" | "resolved";
  severity?: string;
  start?: string;
  end?: string;
};

export async function getActiveAlertSummary(
  tenantId: string,
  workspaceScope?: "all" | null,
): Promise<ActiveAlertSummaryDto> {
  const { data } = await apiClient.get<ActiveAlertSummaryDto>("/alerts/summary/active", {
    params: { tenant_id: tenantId },
    __skipTenantHeader: workspaceScope === "all",
  });
  return data;
}

export async function listAlerts(params: ListAlertsParams): Promise<AlertDto[]> {
  if (!params.deviceId && !params.tenantId && params.workspaceScope !== "all") {
    throw new AppHttpError(0, "At least one of deviceId, tenantId, or workspaceScope=all is required");
  }

  const query: Record<string, string> = {};
  if (params.deviceId) {
    query.device_id = params.deviceId;
  }
  if (params.tenantId) {
    query.tenant_id = params.tenantId;
  }
  if (params.workspaceScope) {
    query.workspace_scope = params.workspaceScope;
  }
  if (params.status) {
    query.status = params.status;
  }
  if (params.severity) {
    query.severity = params.severity;
  }
  if (params.start) {
    query.start = params.start;
  }
  if (params.end) {
    query.end = params.end;
  }

  const { data } = await apiClient.get<AlertDto[]>("/alerts", {
    params: query,
    __skipTenantHeader: params.workspaceScope === "all",
  });
  return data;
}

export async function getAlert(alertId: string): Promise<AlertDetailDto> {
  const { data } = await apiClient.get<AlertDetailDto>(`/alerts/${alertId}`);
  return data;
}

export async function createAlertRule(body: AlertRuleCreateDto): Promise<AlertRuleDto> {
  const { data } = await apiClient.post<AlertRuleDto>("/alerts/rules", body);
  return data;
}

export async function listAlertRules(
  tenantId?: string | null,
  workspaceScope?: "all" | null,
): Promise<AlertRuleDto[]> {
  const params: Record<string, string> = {};
  if (tenantId) {
    params.tenant_id = tenantId;
  }
  if (workspaceScope === "all") {
    params.workspace_scope = "all";
  }
  const { data } = await apiClient.get<AlertRuleDto[]>("/alerts/rules", {
    params: Object.keys(params).length ? params : undefined,
    __skipTenantHeader: workspaceScope === "all",
  });
  return data;
}

export async function getAlertRule(ruleId: string): Promise<AlertRuleDto> {
  const { data } = await apiClient.get<AlertRuleDto>(`/alerts/rules/${ruleId}`);
  return data;
}

export async function updateAlertRule(ruleId: string, body: Partial<AlertRuleCreateDto>): Promise<AlertRuleDto> {
  const { data } = await apiClient.put<AlertRuleDto>(`/alerts/rules/${ruleId}`, body);
  return data;
}

export async function deleteAlertRule(ruleId: string): Promise<void> {
  await apiClient.delete(`/alerts/rules/${ruleId}`);
}
